import UUIDBase from '../UUIDBase'
import User from '../user/User'
import Customer from './Customer'
import Appointment from "@/models/customer/Appointment";
import Insurance from "@/models/insurance/Insurance";

export class ExaminationNote extends UUIDBase {
  static ENDPOINT = '/examination/examinations/notes'

  examinationID = ''
  userID = ''
  user: User | null = null
  note = ''
  // Set when the note was created via an API key scoped to a doctor (e.g. doctorx);
  // show this instead of the clinic user's name.
  doctorName = ''

  static from(json: Record<string, unknown> | ExaminationNote) {
    return Object.assign(new ExaminationNote(), json)
  }
}

export class ExaminationAttachment extends UUIDBase {
  static ENDPOINT = '/examination/examinations/attachments'

  examinationID = ''
  filename = ''
  originalFilename = ''
  mimetype = ''

  static from(json: Record<string, unknown> | ExaminationAttachment) {
    const o = Object.assign(new ExaminationAttachment(), json)
    o.createdAt = new Date(json.createdAt as string)
    o.updatedAt = new Date(json.updatedAt as string)
    o.deletedAt = json.deletedAt ? new Date(json.deletedAt as string) : undefined
    return o
  }
}

export const examinationI18nMessages = {
  en: {
    optometrist: 'Optometrist',
    examinationDate: 'Examination Date',
    examinationNumber: 'Examination Number',
    exam: 'Exam',
    nextAppointment: "Next Appointment"
  },
  ar: {
    optometrist: 'الفاحص',
    examinationDate: 'تاريخ الفحص',
    examinationNumber: 'رقم الفحص',
    exam: 'االفحص',
    nextAppointment: "الموعد القادم"
  }
}
export const EXAM_TYPE_EYE_EXAM = 'eye_exam'
export const EXAM_TYPE_RETINOSCOPY = 'retinoscopy'
export type ExamType = typeof EXAM_TYPE_EYE_EXAM | typeof EXAM_TYPE_RETINOSCOPY

export interface RetinoscopyRefraction {
  odSPH: number | null
  odCYL: number | null
  odAXIS: number | null
  osSPH: number | null
  osCYL: number | null
  osAXIS: number | null
}

export interface RetinoscopyVisualAcuity {
  odWithoutGlasses: string | null
  odWithGlasses: string | null
  osWithoutGlasses: string | null
  osWithGlasses: string | null
}

// workingDistance is the (negative) working-distance lens: Net = Gross + workingDistance
export interface RetinoscopyMeridian {
  gross: number | null
  workingDistance: number | null
  net: number | null
}

// meridian90 lies along axis (90° when null); meridian180 is always 90° away from it
export interface RetinoscopyCross {
  axis?: number | null
  meridian90: RetinoscopyMeridian
  meridian180: RetinoscopyMeridian
}

export interface RetinoscopyEyes {
  od: RetinoscopyCross
  os: RetinoscopyCross
  // Cylinder form picked for this section's Rx; unset follows the cylinder notation setting
  cylNotation?: CylNotation | null
}

// dryRefraction, cycloplegicRefraction and visualAcuity are no longer shown; they stay so old exams keep their data
export interface Retinoscopy {
  dryRefraction: RetinoscopyRefraction
  cycloplegicRefraction: RetinoscopyRefraction
  visualAcuity: RetinoscopyVisualAcuity
  dryRetinoscopy: RetinoscopyEyes
  cycloplegicRetinoscopy: RetinoscopyEyes
  // Retinoscopy notes, kept apart from the exam notes on an Eye Exam that includes a Retinoscopy
  notes: string | null
}

const emptyRefraction = (): RetinoscopyRefraction => ({
  odSPH: null, odCYL: null, odAXIS: null, osSPH: null, osCYL: null, osAXIS: null
})
const emptyMeridian = (): RetinoscopyMeridian => ({gross: null, workingDistance: null, net: null})
const emptyCross = (): RetinoscopyCross => ({axis: null, meridian90: emptyMeridian(), meridian180: emptyMeridian()})
const emptyEyes = (): RetinoscopyEyes => ({od: emptyCross(), os: emptyCross()})

export function emptyRetinoscopy(): Retinoscopy {
  return {
    dryRefraction: emptyRefraction(),
    cycloplegicRefraction: emptyRefraction(),
    visualAcuity: {odWithoutGlasses: null, odWithGlasses: null, osWithoutGlasses: null, osWithGlasses: null},
    dryRetinoscopy: emptyEyes(),
    cycloplegicRetinoscopy: emptyEyes(),
    notes: null
  }
}

// Fills any missing nested objects so templates can bind without null checks.
export function normalizeRetinoscopy(value: Partial<Retinoscopy> | null | undefined): Retinoscopy {
  const base = emptyRetinoscopy()
  if (!value) return base
  const eyes = (src: Partial<RetinoscopyEyes> | undefined, dst: RetinoscopyEyes) => {
    for (const eye of ['od', 'os'] as const) {
      dst[eye].axis = normalizeRetinoscopyAxis(src?.[eye]?.axis)
      for (const m of ['meridian90', 'meridian180'] as const) {
        Object.assign(dst[eye][m], src?.[eye]?.[m] ?? {})
      }
    }
    dst.cylNotation = sectionCylNotation(src, null)
    return dst
  }
  return {
    dryRefraction: {...base.dryRefraction, ...value.dryRefraction},
    cycloplegicRefraction: {...base.cycloplegicRefraction, ...value.cycloplegicRefraction},
    visualAcuity: {...base.visualAcuity, ...value.visualAcuity},
    dryRetinoscopy: eyes(value.dryRetinoscopy, base.dryRetinoscopy),
    cycloplegicRetinoscopy: eyes(value.cycloplegicRetinoscopy, base.cycloplegicRetinoscopy),
    notes: value.notes ?? null
  }
}

// 0° and 180° are the same meridian, so axes are kept in 1–180
export function normalizeRetinoscopyAxis(value: unknown): number | null {
  if (value === '' || value == null) return null
  const n = Math.round(Number(value))
  if (isNaN(n)) return null
  const axis = n % 180
  return axis <= 0 ? axis + 180 : axis
}

// [axis of meridian90, axis of meridian180]
export function retinoscopyAxes(cross: RetinoscopyCross): [number, number] {
  const a = normalizeRetinoscopyAxis(cross.axis) ?? 90
  return [a, normalizeRetinoscopyAxis(a + 90) as number]
}

export const RETINOSCOPY_WORKING_DISTANCES = [-1, -1.25, -1.5, -2, -2.5, -4]

const round2 = (value: number) => Math.round(value * 100) / 100

export function retinoscopyNet(m: RetinoscopyMeridian): number | null {
  if (m.gross == null || m.workingDistance == null) return null
  return round2(m.gross + m.workingDistance)
}

export function formatDiopter(value: number | null | undefined): string {
  if (value == null) return ''
  return value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2)
}

export function workingDistanceLabel(wd: number): string {
  return `${wd.toFixed(2)} (${Math.round(100 / -wd)} cm)`
}

const crossMeridians = (eyes: RetinoscopyEyes): RetinoscopyMeridian[] =>
    (['od', 'os'] as const).flatMap(eye => [eyes[eye].meridian90, eyes[eye].meridian180])

// Distinct working distances used in a retinoscopy section (normally exactly one)
export function retinoscopyWorkingDistances(eyes: RetinoscopyEyes): number[] {
  return [...new Set(crossMeridians(eyes).map(m => m.workingDistance).filter((v): v is number => v != null))]
}

export function setRetinoscopyWorkingDistance(eyes: RetinoscopyEyes, wd: number | null) {
  crossMeridians(eyes).forEach(m => {
    m.workingDistance = wd
  })
}

// A Gross value needs the working distance to give a Net
export function retinoscopyMissingWorkingDistance(eyes: RetinoscopyEyes): boolean {
  return crossMeridians(eyes).some(m => m.gross != null && m.workingDistance == null)
}

export function retinoscopyHasValues(eyes: RetinoscopyEyes): boolean {
  return crossMeridians(eyes).some(m => m.gross != null)
}

// Whether a Retinoscopy section was filled in (crosses or its notes)
export function retinoscopyIsFilled(retinoscopy: Retinoscopy | null | undefined): boolean {
  if (!retinoscopy) return false
  return retinoscopyHasValues(retinoscopy.dryRetinoscopy)
      || retinoscopyHasValues(retinoscopy.cycloplegicRetinoscopy)
      || !!retinoscopy.notes?.trim()
}

export interface RetinoscopyRx {
  sph: number
  cyl: number
  axis: number
}

export type CylNotation = 'minus' | 'plus'

export const CYL_NOTATION_SETTING_KEY = 'examination_retinoscopy_cyl_notation'

export function cylNotationFromSetting(value: unknown): CylNotation {
  return value === 'plus' ? 'plus' : 'minus'
}

export function sectionCylNotation<T extends CylNotation | null>(eyes: Partial<RetinoscopyEyes> | null | undefined, fallback: T): CylNotation | T {
  const value = eyes?.cylNotation
  return value === 'plus' || value === 'minus' ? value : fallback
}

export function transposeRx(rx: RetinoscopyRx): RetinoscopyRx {
  if (rx.cyl === 0) return rx
  return {sph: round2(rx.sph + rx.cyl), cyl: round2(-rx.cyl), axis: (rx.axis + 89) % 180 + 1}
}

// Rx from the net powers of the cross's two meridians, in minus- or plus-cylinder form
export function retinoscopyRx(cross: RetinoscopyCross, notation: CylNotation = 'minus'): RetinoscopyRx | null {
  const p90 = cross.meridian90.net
  const p180 = cross.meridian180.net
  if (p90 == null || p180 == null) return null
  const [a, b] = retinoscopyAxes(cross)
  const minus = p90 >= p180
      ? {sph: p90, cyl: round2(p180 - p90), axis: a}
      : {sph: p180, cyl: round2(p90 - p180), axis: b}
  return notation === 'plus' ? transposeRx(minus) : minus
}

// Refraction fields predicted from a retinoscopy section's Rx; an eye without a full Rx is left out
export function refractionFromRetinoscopy(eyes: RetinoscopyEyes, notation: CylNotation = 'minus'): Partial<RetinoscopyRefraction> {
  const out: Partial<RetinoscopyRefraction> = {}
  for (const eye of ['od', 'os'] as const) {
    const rx = retinoscopyRx(eyes[eye], sectionCylNotation(eyes, notation))
    if (!rx) continue
    out[`${eye}SPH`] = rx.sph
    out[`${eye}CYL`] = rx.cyl
    out[`${eye}AXIS`] = rx.cyl === 0 ? null : rx.axis
  }
  return out
}

export function formatRx(rx: RetinoscopyRx | null): string {
  if (!rx) return ''
  if (rx.cyl === 0) return `${formatDiopter(rx.sph)} DS`
  return `${formatDiopter(rx.sph)} / ${formatDiopter(rx.cyl)} × ${String(rx.axis).padStart(3, '0')}`
}

// Regular Eye Exam helpers
export type EyeExamSection = 'exam' | 'distance' | 'reading' | 'contact' | 'prism'

export const EYE_EXAM_SECTION_FIELDS: Record<EyeExamSection, string[]> = {
  exam: ['vaWithoutGlassesOD', 'vaWithoutGlassesOS', 'vaWithGlassesOD', 'vaWithGlassesOS', 'iopOD', 'iopOS', 'iopODStatus', 'iopOSStatus'],
  distance: ['distanceODSPH', 'distanceODCYL', 'distanceODAXIS', 'distanceODIPD', 'distanceOSSPH', 'distanceOSCYL', 'distanceOSAXIS'],
  reading: ['readingODSPH', 'readingODCYL', 'readingODAXIS', 'readingODIPD', 'readingOSSPH', 'readingOSCYL', 'readingOSAXIS'],
  contact: [
    'contactODSPH', 'contactODCYL', 'contactODAXIS', 'contactOSSPH', 'contactOSCYL', 'contactOSAXIS',
    'contactODK1', 'contactOSK1', 'contactODK2', 'contactOSK2', 'contactODKAVE', 'contactOSKAVE',
    'contactODBC', 'contactOSBC', 'contactODDIA', 'contactOSDIA'
  ],
  prism: ['prismOD', 'prismOS', 'prismODBASE', 'prismOSBASE', 'prismODAXIS', 'prismOSAXIS']
}

export function eyeExamFieldsHaveValues(exam: object | null | undefined, fields: string[]): boolean {
  if (!exam) return false
  const record = exam as Record<string, unknown>
  return fields.some(f => record[f] != null && record[f] !== '' && !(typeof record[f] === 'number' && isNaN(record[f] as number)))
}

export function eyeExamSectionHasValues(exam: object | null | undefined, section: EyeExamSection): boolean {
  return eyeExamFieldsHaveValues(exam, EYE_EXAM_SECTION_FIELDS[section])
}

// Any Eye Exam value entered (all sections or notes); an Eye Exam page without any is not printed next to a Retinoscopy
export function eyeExamHasValues(exam: { notes?: string | null } | null | undefined): boolean {
  if (!exam) return false
  return (Object.keys(EYE_EXAM_SECTION_FIELDS) as EyeExamSection[]).some(section => eyeExamSectionHasValues(exam, section))
      || !!exam.notes?.trim()
}

export function roundDiopter(value: number): number {
  return Math.round(value * 100) / 100
}

// Near SPH when the reading SPH is an ADD over the distance SPH (no distance SPH = plano)
export function nearSPH(distanceSPH: number | null | undefined, add: number | null | undefined): number | null {
  if (add == null || isNaN(add)) return null
  return roundDiopter((distanceSPH ?? 0) + add)
}

// Near PD suggested from the distance PD (-2 mm below 70, -3 mm from 70)
export function suggestedReadingPD(distancePD: number | null | undefined): number | null {
  if (distancePD == null || isNaN(distancePD)) return null
  return distancePD < 70 ? distancePD - 2 : distancePD - 3
}

export function keratometryAverage(k1: number | null | undefined, k2: number | null | undefined): number | null {
  if (k1 == null || k2 == null || isNaN(k1) || isNaN(k2)) return null
  return roundDiopter((k1 + k2) / 2)
}

// "+1.25 / -0.50 × 090", or "+1.25 DS" without a cylinder
export function formatSphCylAxis(sph: number | null | undefined, cyl: number | null | undefined, axis: number | null | undefined): string {
  if (sph == null && cyl == null) return ''
  const sphText = formatDiopter(sph ?? 0)
  if (cyl == null || cyl === 0) return `${sphText} DS`
  const axisText = axis == null ? '' : ` × ${String(axis).padStart(3, '0')}`
  return `${sphText} / ${formatDiopter(cyl)}${axisText}`
}

export interface ExaminationPaginated {
  data: Array<Examination>,
  total: number
}

export function hasRetinoscopyPart(exam?: { examType?: string, withRetinoscopy?: boolean }): boolean {
  return exam?.examType === EXAM_TYPE_RETINOSCOPY || !!exam?.withRetinoscopy
}

export default class Examination extends UUIDBase {
  static from(json: Record<string, unknown> | Examination) {
    const exam = Object.assign(new Examination(), json)
    exam.examType = exam.examType || EXAM_TYPE_EYE_EXAM
    exam.retinoscopy = normalizeRetinoscopy(exam.retinoscopy)
    return exam
  }
  static ENDPOINT = '/examination/examinations'

  examType: ExamType = EXAM_TYPE_EYE_EXAM
  retinoscopy: Retinoscopy = emptyRetinoscopy()
  // Eye Exam saved with a Retinoscopy from the same visit: one exam number for both
  withRetinoscopy = false

  vaWithoutGlassesOS: string | undefined
  vaWithoutGlassesOD: string | undefined
  vaWithGlassesOS: string | undefined
  vaWithGlassesOD: string | undefined
  // VA measured with a Catford drum (printed under Visual Acuity)
  vaCatfordDrum = false
  notes: string | undefined = ''
  customerID: string | undefined
  customer: Customer | undefined

  userID: string | undefined
  user: User | undefined

  examinatorID: string | undefined
  doctor: string = ""
  examinator: User | undefined

  // I.O.P
  iopOD: number | undefined
  iopODStatus: string | undefined
  iopOS: number | undefined
  iopOSStatus: string | undefined

  // Distance Glasses Data
  distanceODSPH: number | undefined
  distanceODCYL: number | undefined
  distanceODAXIS: number | undefined
  distanceODIPD: number | undefined
  distanceOSSPH: number | undefined
  distanceOSCYL: number | undefined
  distanceOSAXIS: number | undefined

  prismOD: number | undefined
  prismOS: number | undefined
  prismODBASE: string | undefined
  prismOSBASE: string | undefined
  prismODAXIS: number | undefined
  prismOSAXIS: number | undefined

  // Reading Glasses Data
  readingODSPH: number | undefined
  readingODCYL: number | undefined
  readingODAXIS: number | undefined
  readingODIPD: number | undefined
  readingOSSPH: number | undefined
  readingOSCYL: number | undefined
  readingOSAXIS: number | undefined
  readingAdd: boolean | undefined = true

  // Contact Lenses Data
  contactODSPH: number | undefined
  contactODCYL: number | undefined
  contactODAXIS: number | undefined

  contactOSSPH: number | undefined
  contactOSCYL: number | undefined
  contactOSAXIS: number | undefined

  contactODK1: number | undefined
  contactOSK1: number | undefined
  contactODK2: number | undefined
  contactOSK2: number | undefined
  contactODKAVE: number | undefined
  contactOSKAVE: number | undefined
  contactODBC: number | undefined
  contactOSBC: number | undefined
  contactODDIA: number | undefined
  contactOSDIA: number | undefined

  appointmentID: string | undefined
  appointment: Appointment | undefined
  nextAppointment: string
  isStarred: boolean;

  insuranceID: string | undefined
  insurance: Insurance | undefined

  examinationAttachments = new Array<ExaminationAttachment>()
}
